import { useState } from "react";
import { Link } from "react-router-dom";

const services = [
  {
    icon: "ri-layout-masonry-line",
    title: "Website Design",
    description: "Stunning, conversion-focused designs that reflect your brand and captivate your audience from the first click.",
    features: ["Custom UI/UX Design", "Brand-aligned Aesthetics", "Mobile-first Approach", "Wireframing & Prototyping"],
    color: "from-[#d4af37]/20 to-transparent",
  },
  {
    icon: "ri-code-s-slash-line",
    title: "Website Development",
    description: "Lightning-fast, technically flawless websites built with modern frameworks for peak performance and reliability.",
    features: ["React / Next.js Builds", "CMS Integration", "API Connections", "Performance Optimization"],
    color: "from-[#4a6fa5]/20 to-transparent",
  },
  {
    icon: "ri-search-eye-line",
    title: "SEO Optimization",
    description: "Get found by the right people at the right time. We engineer your site to rank and attract organic traffic.",
    features: ["Technical SEO Audit", "On-page Optimization", "Speed & Core Web Vitals", "Local SEO Strategy"],
    color: "from-[#5a8a5a]/20 to-transparent",
  },
  {
    icon: "ri-line-chart-line",
    title: "Conversion Optimization",
    description: "Turn more visitors into paying clients with strategic CRO — from copy to layout to call-to-action placement.",
    features: ["A/B Testing", "Funnel Analysis", "CTA Strategy", "User Behavior Insights"],
    color: "from-[#8a5a8a]/20 to-transparent",
  },
];

export default function ServicesSection() {
  const [hovered, setHovered] = useState<number | null>(null);

  return (
    <section id="services" className="py-28 bg-[#080b18]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16">
          <div>
            <span className="text-[#d4af37] text-xs font-semibold tracking-widest uppercase mb-4 block">What We Do</span>
            <h2 className="text-4xl md:text-5xl font-black text-white leading-tight">
              Services Built for<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d4af37] to-[#f0d060]">Real Results</span>
            </h2>
          </div>
          <p className="text-white/50 text-base max-w-sm leading-relaxed lg:text-right">
            Every service we offer is engineered to move the needle on what matters most — your growth.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {services.map((service, index) => (
            <div
              key={service.title}
              className={`relative group p-8 rounded-2xl border transition-all duration-300 cursor-default overflow-hidden ${
                hovered === index
                  ? "border-[#d4af37]/40 bg-[#0f1220]"
                  : "border-white/5 bg-[#0c0f1c] hover:border-white/10"
              }`}
              onMouseEnter={() => setHovered(index)}
              onMouseLeave={() => setHovered(null)}
            >
              {/* Gradient bg */}
              <div className={`absolute inset-0 bg-gradient-to-br ${service.color} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}></div>

              <div className="relative z-10">
                {/* Icon */}
                <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-[#d4af37]/10 border border-[#d4af37]/20 mb-6 group-hover:bg-[#d4af37]/20 transition-colors duration-300">
                  <i className={`${service.icon} text-[#d4af37] text-2xl`}></i>
                </div>

                {/* Title */}
                <h3 className="text-xl font-bold text-white mb-3">{service.title}</h3>

                {/* Description */}
                <p className="text-white/50 text-sm leading-relaxed mb-6">{service.description}</p>

                {/* Features */}
                <ul className="space-y-2">
                  {service.features.map((feature) => (
                    <li key={feature} className="flex items-center gap-2 text-white/40 text-sm">
                      <i className="ri-check-line text-[#d4af37] text-xs"></i>
                      {feature}
                    </li>
                  ))}
                </ul>

                {/* Arrow */}
                <Link
                  to="/services"
                  className="mt-6 flex items-center gap-2 text-[#d4af37] text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300 cursor-pointer w-fit"
                >
                  Learn More <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
